import { useEffect } from 'react';

interface PageMeta {
  title: string;
  description: string;
  /** Absolute canonical URL, e.g. https://add-individual-solutions.com/cx */
  canonical: string;
  ogType?: 'website' | 'article';
}

const SITE_TITLE = 'ADD Individual Solutions — Our Vision, Your Way';

const setMeta = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/**
 * Sets document title, description, canonical and Open Graph tags for a route,
 * and restores the index.html defaults when the page unmounts — so navigating
 * back to the home page never keeps a sub-page title in the tab.
 */
export const usePageMeta = ({ title, description, canonical, ogType = 'website' }: PageMeta) => {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: document.head.querySelector<HTMLMetaElement>('meta[name="description"]')?.getAttribute('content') ?? '',
      canonical: document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.getAttribute('href') ?? 'https://add-individual-solutions.com/',
      ogTitle: document.head.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.getAttribute('content') ?? SITE_TITLE,
      ogDescription: document.head.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.getAttribute('content') ?? '',
      ogUrl: document.head.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.getAttribute('content') ?? 'https://add-individual-solutions.com/',
      ogType: document.head.querySelector<HTMLMetaElement>('meta[property="og:type"]')?.getAttribute('content') ?? 'website',
      twTitle: document.head.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.getAttribute('content') ?? SITE_TITLE,
      twDescription: document.head.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.getAttribute('content') ?? '',
    };

    document.title = title;
    setMeta('meta[name="description"]', 'name', 'description', description);
    setCanonical(canonical);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonical);
    setMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);

    return () => {
      document.title = prev.title;
      setMeta('meta[name="description"]', 'name', 'description', prev.description);
      setCanonical(prev.canonical);
      setMeta('meta[property="og:title"]', 'property', 'og:title', prev.ogTitle);
      setMeta('meta[property="og:description"]', 'property', 'og:description', prev.ogDescription);
      setMeta('meta[property="og:url"]', 'property', 'og:url', prev.ogUrl);
      setMeta('meta[property="og:type"]', 'property', 'og:type', prev.ogType);
      setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', prev.twTitle);
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', prev.twDescription);
    };
  }, [title, description, canonical, ogType]);
};
